import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AccountScreen } from '../screens/account/AccountScreen.tsx';
import { AuthCallback } from '../screens/account/AuthCallback.tsx';
import { DeleteAccountScreen, LegalScreen } from '../screens/account/LegalScreen.tsx';
import { PrivacyScreen } from '../screens/account/PrivacyScreen.tsx';
import { ProfileScreen } from '../screens/account/ProfileScreen.tsx';
import { SecurityScreen } from '../screens/account/SecurityScreen.tsx';
import { CollectionScreen } from '../screens/meta/CollectionScreen.tsx';
import { DailyScreen } from '../screens/meta/DailyScreen.tsx';
import { LeaderboardScreen } from '../screens/meta/LeaderboardScreen.tsx';
import { NewsScreen } from '../screens/meta/NewsScreen.tsx';
import { ProgressScreen } from '../screens/meta/ProgressScreen.tsx';
import { ReplayScreen } from '../screens/meta/ReplayScreen.tsx';
import { OnlineHub, OnlineJoin } from '../screens/online/OnlineHub.tsx';
import { FriendsScreen } from '../screens/social/FriendsScreen.tsx';
import { InviteBanner } from '../screens/social/InviteBanner.tsx';
import { UpdateBanner } from '../pwa/UpdateBanner.tsx';
import { useSocialSync } from '../social/socialStore.ts';
import { useAnalytics } from '../analytics/useAnalytics.ts';
import { RoomScreen } from '../screens/online/RoomScreen.tsx';
import { Home } from '../screens/Home.tsx';
import { ComingSoon } from '../screens/ComingSoon.tsx';
import { DesignSystem } from '../screens/DesignSystem.tsx';
import { FirstRun } from '../screens/FirstRun.tsx';
import { NotFound } from '../screens/NotFound.tsx';
import { Play } from '../screens/Play.tsx';
import { SoloSetup } from '../screens/play/SoloSetup.tsx';
import { TrainingSetup } from '../screens/play/TrainingSetup.tsx';
import { TutorialMenu } from '../screens/play/TutorialMenu.tsx';
import { SettingsScreen } from '../screens/settings/SettingsScreen.tsx';
import { Splash } from '../screens/Splash.tsx';
import { useAppStore } from '../state/appStore.ts';
import { useSettings } from '../state/settingsStore.ts';
import { useGamepadNavigation } from '../systems/input/useGamepadNavigation.ts';
import { GameScreen } from '../game/GameScreen.tsx';
import { TrainingScreen } from '../game/TrainingScreen.tsx';
import { audio } from '../audio/manager.ts';
import { Toaster } from '../ui/index.ts';

// The team's area is a chunk of its own: nothing of it is in the page a player loads.
const AdminRoutes = lazy(() => import('../admin/AdminRoutes.tsx'));

const galleryEnabled = import.meta.env.DEV || import.meta.env.VITE_ENABLE_GALLERY === 'true';

/** Shared chrome: skip link, toasts, gamepad navigation, and the first-run redirect. */
function AppLayout() {
  const { t } = useTranslation();
  const { firstRunDone } = useSettings();
  const location = useLocation();
  useGamepadNavigation();
  useSocialSync();
  useAnalytics();

  // The menus have their own music; the games choose theirs.
  const inGame = location.pathname === '/game' || location.pathname === '/training';
  useEffect(() => {
    if (!inGame) audio.setScene('menu');
  }, [inGame]);

  // After every navigation, move focus to the new page's <main>. Without this, focus is lost
  // (or lands on a stray element) when the clicked control disappears, which breaks keyboard
  // and screen-reader flow.
  useEffect(() => {
    document.getElementById('main')?.focus({ preventScroll: true });
  }, [location.pathname]);

  // The legal pages and the page about deleting an account are public: readable before the first run.
  const isPublic =
    location.pathname.startsWith('/legal/') || location.pathname === '/delete-account';
  if (!firstRunDone && location.pathname !== '/welcome' && !isPublic) {
    return <Navigate to="/welcome" replace />;
  }
  return (
    <>
      <a className="skip-link" href="#main">
        {t('skipToContent')}
      </a>
      <Outlet />
      <InviteBanner />
      <UpdateBanner />
      <Toaster />
    </>
  );
}

export function App() {
  const booted = useAppStore((s) => s.booted);
  if (!booted) return <Splash />;
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<Home />} />
          <Route path="welcome" element={<FirstRun />} />
          <Route path="play" element={<Play />} />
          <Route path="play/solo" element={<SoloSetup />} />
          <Route path="play/training" element={<TrainingSetup />} />
          <Route path="play/tutorial" element={<TutorialMenu />} />
          <Route path="game" element={<GameScreen />} />
          <Route path="training" element={<TrainingScreen />} />
          <Route path="settings" element={<SettingsScreen />} />
          <Route path="online" element={<OnlineHub />} />
          <Route path="online/room" element={<RoomScreen />} />
          <Route path="online/join/:code" element={<OnlineJoin />} />
          <Route path="friends" element={<FriendsScreen />} />
          <Route
            path="admin/*"
            element={
              <Suspense fallback={null}>
                <AdminRoutes />
              </Suspense>
            }
          />
          <Route path="progress" element={<ProgressScreen />} />
          <Route path="collection" element={<CollectionScreen />} />
          <Route path="leaderboards" element={<LeaderboardScreen />} />
          <Route path="news" element={<NewsScreen />} />
          <Route path="daily" element={<DailyScreen />} />
          <Route path="replay/:id" element={<ReplayScreen />} />
          <Route path="account" element={<AccountScreen />} />
          <Route path="account/profile" element={<ProfileScreen />} />
          <Route path="account/security" element={<SecurityScreen />} />
          <Route path="account/privacy" element={<PrivacyScreen />} />
          <Route path="auth/callback" element={<AuthCallback />} />
          <Route path="legal/:doc" element={<LegalScreen />} />
          <Route path="delete-account" element={<DeleteAccountScreen />} />
          <Route path="soon/:feature" element={<ComingSoon />} />
          {galleryEnabled ? <Route path="design-system" element={<DesignSystem />} /> : null}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
